import React, { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { CONSTANTS } from 'config/constants';
import { useMutation } from '@apollo/client';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { modifyConstructionSettingsTemplate } from 'apollo/templates/user';
import { ConstructionSetting } from 'models/construction-settings';

const ConstructionSettings: FC = () => {
    const [constructionSetting, setConstructionSettings] = useState({
        ponticDesign: '',
        metalDesign: '',
        occlusionContact: '',
        contactType: '',
    });

    const [changesSaved, setChangesSaved] = useState(false);
    const { t: translate } = useTranslation(['user-settings', 'construction-props']);
    const currentOrganization = currentOrgVar();
    const currentUser = currentUserVar();

    const pointicDesignOptions = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.PONTIC_DESIGN,
    );

    const metalDesignOptions = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.METAL_DESIGN,
    );

    const occlusionOption = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.OCCLUSION_TYPE,
    );

    const contactTypeOption = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CONTACT_TYPE,
    );

    useEffect(() => {
        setConstructionSettings({
            ...constructionSetting,
            ponticDesign: !currentUser?.constructionSetting?.ponticDesign
                ? pointicDesignOptions[1].name
                : (currentUser?.constructionSetting?.ponticDesign as string),
            metalDesign: !currentUser?.constructionSetting?.metalDesign
                ? metalDesignOptions[0].name
                : (currentUser?.constructionSetting?.metalDesign as string),
            occlusionContact: !currentUser?.constructionSetting?.occlusionContact
                ? occlusionOption[1]
                : (currentUser?.constructionSetting?.occlusionContact as string),
            contactType: !currentUser?.constructionSetting?.contactType
                ? contactTypeOption[1]
                : (currentUser?.constructionSetting?.contactType as string),
        });
    }, [currentUser]);

    const [modifyConstructionSettings, { error }] = useMutation(modifyConstructionSettingsTemplate, {
        onCompleted: ({ modifyConstructionSettings }) => {
            currentUser.constructionSetting = modifyConstructionSettings.constructionSetting;
            currentUserVar(currentUser);
        },
    });
    const handleSaveChanges = () => {
        setChangesSaved(true);
        modifyConstructionSettings({
            variables: {
                ponticDesign: constructionSetting.ponticDesign,
                metalDesign: constructionSetting.metalDesign,
                occlusionContact: constructionSetting.occlusionContact,
                contactType: constructionSetting.contactType,
            },
        });
    };
    const handleChange = (event: SelectChangeEvent<string>) => {
        const { name, value } = event.target;
        setConstructionSettings((prevSettings) => ({
            ...prevSettings,
            [name]: value as string,
        }));
    };

    return (
        <Fragment>
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}

            <PageTitle headingStyle="h2" title={translate('user-settings:CONSTRUCTION_SETTINGS')} />
            <Box mb={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('construction-props:PONTIC_DESIGN')}</InputLabel>
                            <Select
                                label={translate('construction-props:PONTIC_DESIGN')}
                                value={constructionSetting.ponticDesign}
                                onChange={handleChange}
                                name="ponticDesign"
                            >
                                {pointicDesignOptions.map((option: ConstructionSetting) => (
                                    <MenuItem key={option._id} value={option.name}>
                                        {translate(`construction-props:${option.name}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('construction-props:METAL_DESIGN')}</InputLabel>
                            <Select
                                label={translate('construction-props:METAL_DESIGN')}
                                value={constructionSetting.metalDesign}
                                onChange={handleChange}
                                name="metalDesign"
                            >
                                {metalDesignOptions.map((option: ConstructionSetting) => (
                                    <MenuItem key={String(option)} value={option.name}>
                                        {translate(`construction-props:${option.name}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('construction-props:OCCLUSION_TYPE')}</InputLabel>
                            <Select
                                label={translate('construction-props:OCCLUSION_TYPE')}
                                value={constructionSetting.occlusionContact}
                                onChange={handleChange}
                                name="occlusionContact"
                            >
                                {occlusionOption.map((option: string) => (
                                    <MenuItem key={option} value={option}>
                                        {translate(`construction-props:${option}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>{translate('construction-props:CONTACT_TYPE')}</InputLabel>
                            <Select
                                label={translate('construction-props:CONTACT_TYPE')}
                                value={constructionSetting.contactType}
                                onChange={handleChange}
                                name="contactType"
                            >
                                {contactTypeOption.map((option: string) => (
                                    <MenuItem key={option} value={option}>
                                        {translate(`construction-props:${option}`)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Box>

            <Box display="flex" justifyContent="center">
                <Button variant="contained" onClick={handleSaveChanges}>
                    {translate('user-settings:SAVE_CHANGES')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default ConstructionSettings;
